/** @jsxImportSource theme-ui @jsxRuntime classic */

import {
  Box,
  SecondaryMenuBar,
  WideCta,
  Text,
  Container,
  IntrinsicImage,
  Link,
  Section,
  Grid,
  Flex,
  CarouselV2ContextProvider,
  CarouselNavigationV2,
  CarouselV2,
  useCarouselLeftSpacing,
  useThemeUIValue,
  AnchorSection,
} from '@tishman/components';
import { graphql } from 'gatsby';
import invariant from 'invariant';
import { H } from '@hzdg/sectioning';
import React, { useMemo } from 'react';

import {
  TicketOfferingTicket,
  TicketOfferings,
} from '../../components/TicketOfferings';
import { Layout } from '../../layouts';
import { usePageData } from '../../utils';
import {
  TicketOffering,
  getIncludedTicketsArray,
} from '../../utils/get-included-tickets';

import FeaturedArticle from './featured-article';
import TheLoungeAtRockefellerCenter from './the-lounge';
import PrivateDiningAndVenues from './private-dining';
import HeroSection from './hero-section';

import type { IGatsbyImageData } from 'gatsby-plugin-image';
import type { PageConfig } from '../../PageConfig';

export const pageConfig: PageConfig = {
  theme: 'Rock Center Black',
  logo: 'VIP Experiences',
  cta: {
    to: '/buy-tickets',
    label: 'Buy tickets',
    sx: {
      fontWeight: 'medium',
      variant: 'buttons.accent',
    },
  },
};

// Function to get included tickets array
const getIncludedTicketsArrayOnce = (
  sanityVipExperienceLp: Queries.VipExperienceQuery['sanityVipExperienceLp'],
) => {
  return getIncludedTicketsArray(
    sanityVipExperienceLp?.ticketOfferings as TicketOffering[],
    sanityVipExperienceLp,
  );
};

export default function VipExperiencePage(): React.JSX.Element {
  const { sanityVipExperienceLp } = usePageData<Queries.VipExperienceQuery>();

  invariant(sanityVipExperienceLp, 'Sanity VIP data is required');

  // TICKET OFFERINGS ARRAY LOGIC
  const includedTicketsArray = useMemo(
    () => getIncludedTicketsArrayOnce(sanityVipExperienceLp),
    [sanityVipExperienceLp],
  );

  // Memoized subnav links
  const subnavLinks = useMemo(
    () =>
      sanityVipExperienceLp?.subnavLinks?.map((link) => ({
        url: link?.url ?? '',
        label: link?.caption ?? '',
      })),
    [sanityVipExperienceLp],
  );

  const slideSpaceBetween = useThemeUIValue([3, 4], 'space');

  const carouselLeftSpacing = useCarouselLeftSpacing([16, 32, 32, 32, 32]);

  return (
    <Layout>
      <SecondaryMenuBar
        cta={pageConfig.cta}
        links={subnavLinks}
        sticky
        threshold={0.5}
        title="VIP EXPERIENCE"
      />
      {sanityVipExperienceLp?.heroSection && (
        <HeroSection
          subtitle={sanityVipExperienceLp.heroSection.subtitle ?? ''}
          title={sanityVipExperienceLp.heroSection.title ?? ''}
        />
      )}
      <TicketOfferings
        id={(sanityVipExperienceLp?.subnavLinks?.[0]?.url ?? '').replace(
          '#',
          '',
        )}
        includedTicketsArray={includedTicketsArray as TicketOfferingTicket[]}
        theme="Rock Center"
        title="Experiences"
      />
      <TheLoungeAtRockefellerCenter
        alt={
          sanityVipExperienceLp?.theLoungeAtRockefellerCenter?.photo?.alt ?? ''
        }
        id={(sanityVipExperienceLp?.subnavLinks?.[1]?.url ?? '').replace(
          '#',
          '',
        )}
        image={
          sanityVipExperienceLp?.theLoungeAtRockefellerCenter?.photo?.asset
            ?.gatsbyImageData as IGatsbyImageData
        }
        subtitle={
          sanityVipExperienceLp?.theLoungeAtRockefellerCenter?.subtitle ?? ''
        }
        title={sanityVipExperienceLp.theLoungeAtRockefellerCenter?.title ?? ''}
      />
      {/* PRIVATE DINING */}
      <PrivateDiningAndVenues
        cards={
          sanityVipExperienceLp?.privateDining
            ?.privateDiningAndVenueCards as Queries.SanityPrivateDiningAndVenueCardType[]
        }
        description={sanityVipExperienceLp?.privateDining?.description ?? ''}
        id={(sanityVipExperienceLp?.subnavLinks?.[2]?.url ?? '').replace(
          '#',
          '',
        )}
        title={sanityVipExperienceLp?.privateDining?.title ?? ''}
      />
      {/* PRIVATE VENUES */}
      <PrivateDiningAndVenues
        cards={
          sanityVipExperienceLp?.privateVenues
            ?.privateDiningAndVenueCards as Queries.SanityPrivateDiningAndVenueCardType[]
        }
        description={sanityVipExperienceLp?.privateVenues?.description ?? ''}
        id={(sanityVipExperienceLp?.subnavLinks?.[2]?.url ?? '').replace(
          '#',
          '',
        )}
        title={sanityVipExperienceLp.privateVenues?.title ?? ''}
      />
      {/* LARGE EVENT CALLOUT */}
      <Section sx={{ m: 'auto', py: 4 }} theme="Rock Center">
        <Container>
          <Grid columns={[1, 1, '2fr 1fr']} gap={2} padding={3}>
            <Grid columns={[1, '1fr 2fr']}>
              <Box sx={{ width: ['100%', '232px'] }}>
                {sanityVipExperienceLp?.largeEventCallout?.photo?.asset
                  ?.gatsbyImageData && (
                  <IntrinsicImage
                    alt={
                      sanityVipExperienceLp?.largeEventCallout?.photo?.alt ?? ''
                    }
                    image={
                      sanityVipExperienceLp?.largeEventCallout?.photo?.asset
                        ?.gatsbyImageData as IGatsbyImageData
                    }
                  />
                )}
              </Box>
              <Box paddingX={3}>
                <Box marginY={3}>
                  <H
                    sx={{
                      variant: 'styles.h3',
                      fontSize: 6,
                      fontFamily: 'headingSecondary',
                    }}
                  >
                    {sanityVipExperienceLp?.largeEventCallout?.title}
                  </H>
                </Box>
                <Box>
                  <Text sx={{ fontSize: 3 }}>
                    {sanityVipExperienceLp?.largeEventCallout?.description}
                  </Text>
                </Box>
              </Box>
            </Grid>
            <Flex
              paddingY={[4, 0]}
              sx={{
                alignItems: 'center',
                flexDirection: ['column', 'row'],
                justifyContent: 'flex-end',
              }}
            >
              {sanityVipExperienceLp?.largeEventCallout?.button1Text && (
                <Link
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxHeight: '58px',
                    width: ['100%', 'auto'],
                  }}
                  to={
                    sanityVipExperienceLp?.largeEventCallout?.button1Link ?? ''
                  }
                  variant="button"
                >
                  {sanityVipExperienceLp?.largeEventCallout?.button1Text}
                </Link>
              )}
              {sanityVipExperienceLp?.largeEventCallout?.button2Text && (
                <Link
                  sx={{
                    border: ['none', '1px solid #000'],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: [0, 3],
                    maxHeight: '58px',
                    width: ['100%', 'auto'],
                  }}
                  to={
                    sanityVipExperienceLp?.largeEventCallout?.button2Link ?? ''
                  }
                  variant="buttonBorder"
                >
                  {sanityVipExperienceLp?.largeEventCallout?.button2Text}
                </Link>
              )}
            </Flex>
          </Grid>
        </Container>
      </Section>
      {/* SHOPPING & EXPERIENCES */}
      <AnchorSection
        id={(sanityVipExperienceLp?.subnavLinks?.[3]?.url ?? '').replace(
          '#',
          '',
        )}
        sx={{
          paddingBottom: '32px',
        }}
        theme="Rock Center Gold"
      >
        {/* CAROUSEL */}
        <CarouselV2ContextProvider>
          <Container
            paddingBottom={[0, 4, '80px']}
            paddingTop={[5, '80px']}
            sx={{
              display: 'flex',
              flexDirection: ['column', 'row'],
              alignItems: ['center', 'flex-start'],
              justifyContent: ['center', 'space-between'],
              flexWrap: 'wrap',
              gap: 3,
            }}
          >
            <Text
              sx={{
                fontSize: [6, 6, 7],
                width: ['100%', 'auto'],
                mb: [3, 0],
                flexShrink: 0,
              }}
              variant="heading"
            >
              {sanityVipExperienceLp?.shoppingAndExperiences?.title}
            </Text>

            <Box
              sx={{
                display: ['none', 'flex', 'flex'],
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: ['center', 'flex-end'],
                width: ['100%', 'auto'],
                minWidth: '80px',
              }}
            >
              <CarouselNavigationV2
                containerStyle={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: 2,
                }}
              />
            </Box>
          </Container>

          <CarouselV2
            slideStyle={{ width: 'auto' }}
            swiperOptions={{
              spaceBetween: slideSpaceBetween,
              style: {
                paddingLeft: carouselLeftSpacing,
                paddingRight: Math.min(32, carouselLeftSpacing),
              },
            }}
          >
            {sanityVipExperienceLp.shoppingAndExperiences?.shopCards?.map(
              (card, index) => {
                if (!card || !card.photo || !card.photo.asset) {
                  return null;
                }
                return (
                  <Box key={index} sx={{ maxWidth: [343, 464] }}>
                    <Text
                      marginBottom={[3, '32px']}
                      sx={{ fontSize: ['18px', '24px'] }}
                      variant="smallTitle"
                    >
                      {card?.title}
                    </Text>
                    <IntrinsicImage
                      alt={card?.photo?.alt ?? ''}
                      image={
                        card?.photo?.asset?.gatsbyImageData as IGatsbyImageData
                      }
                      sx={{ maxHeight: [217, 296] }}
                    />
                    <Text marginTop={3}>{card?.subtitle}</Text>
                    <Link
                      sx={{ display: 'inline-block', mt: [3, '24px'] }}
                      to={card?.linkURL?.url ?? '/shops'}
                      variant="underline"
                    >
                      {card?.linkURL?.caption ?? 'Learn More'}
                    </Link>
                  </Box>
                );
              },
            )}
          </CarouselV2>

          <Box
            sx={{
              display: ['flex', 'none', 'none', 'none', 'none'],
              justifyContent: 'flex-end',
            }}
          >
            <CarouselNavigationV2
              containerStyle={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                gap: 2,
              }}
            />
          </Box>
        </CarouselV2ContextProvider>
      </AnchorSection>
      {/* LITTLE NELL */}
      {sanityVipExperienceLp.additionalCallout && (
        <Box>
          <WideCta
            border
            caption={sanityVipExperienceLp.additionalCallout.caption ?? ''}
            link={{
              url: sanityVipExperienceLp.additionalCallout.link?.url ?? '',
              label:
                sanityVipExperienceLp.additionalCallout.link?.caption ?? '',
            }}
            title={sanityVipExperienceLp.additionalCallout.title ?? ''}
          />
        </Box>
      )}
      {/* FEATURED ARTICLE */}
      {sanityVipExperienceLp.featuredArticle?.featuredStory && (
        <FeaturedArticle
          featuredStory={{
            ...sanityVipExperienceLp.featuredArticle?.featuredStory,
            excerpt:
              sanityVipExperienceLp.featuredArticle?.featuredStory?.excerpt ??
              '',
            publishAt:
              sanityVipExperienceLp.featuredArticle?.featuredStory?.publishAt ??
              '',
            poster: {
              alt:
                sanityVipExperienceLp.featuredArticle?.featuredStory?.poster
                  ?.alt ?? '',
              asset: {
                gatsbyImageData: sanityVipExperienceLp.featuredArticle
                  ?.featuredStory?.poster?.asset
                  ?.gatsbyImageData as IGatsbyImageData,
              },
            },
            titleAndSlug: {
              title:
                sanityVipExperienceLp.featuredArticle?.featuredStory
                  .titleAndSlug?.title ?? '',
              slug: {
                current:
                  sanityVipExperienceLp.featuredArticle?.featuredStory
                    ?.titleAndSlug?.slug?.current ?? '',
              },
            },
          }}
          subtitle={sanityVipExperienceLp.featuredArticle.subtitle ?? ''}
          title={sanityVipExperienceLp.featuredArticle.title ?? ''}
        />
      )}
    </Layout>
  );
}

export const query = graphql`
  query VipExperience {
    meta: sanityVipExperienceLp {
      seo {
        title: metaTitle
        description: metaDescription
        canonical: metaCanonicalUrl
        ogImage: metaImage {
          asset {
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
    sanityVipExperienceLp {
      heroSection {
        subtitle
        title
      }
      ticketOfferings {
        title
        tickets {
          title
          url {
            url
            caption
          }
          image {
            alt
            asset {
              gatsbyImageData(
                layout: CONSTRAINED
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
          description {
            children {
              text
            }
          }
          slugId
        }
      }
      includedTickets {
        slugId
        categoryTitle
        order
      }
      theLoungeAtRockefellerCenter {
        title
        subtitle
        photo {
          alt
          asset {
            gatsbyImageData(width: 1600, layout: CONSTRAINED)
          }
        }
      }
      privateDining {
        title
        description
        privateDiningAndVenueCards {
          callOut
          capacity
          cardTitle
          cardText
          image {
            asset {
              gatsbyImageData(width: 1600, layout: CONSTRAINED)
            }
            alt
          }
          pdf {
            asset {
              url
            }
          }
          cta {
            url
            caption
          }
        }
      }
      privateVenues {
        title
        description
        privateDiningAndVenueCards {
          callOut
          capacity
          cardTitle
          cardText
          image {
            asset {
              gatsbyImageData(width: 1600, layout: CONSTRAINED)
            }
            alt
          }
          pdf {
            asset {
              url
            }
          }
          cta {
            url
            caption
          }
        }
      }
      largeEventCallout {
        title
        button1Link
        button1Text
        button2Link
        button2Text
        description
        photo {
          alt
          asset {
            gatsbyImageData(width: 1600, layout: CONSTRAINED)
          }
        }
      }
      shoppingAndExperiences {
        title
        shopCards {
          title
          subtitle
          linkURL {
            url
            caption
          }
          photo {
            alt
            asset {
              gatsbyImageData(width: 1600, layout: CONSTRAINED)
            }
          }
        }
      }
      featuredArticle {
        title
        subtitle
        featuredStory {
          poster {
            alt
            asset {
              gatsbyImageData(width: 1600, layout: CONSTRAINED)
            }
          }
          excerpt
          publishAt
          titleAndSlug {
            title
            slug {
              current
            }
          }
        }
      }
      additionalCallout {
        link {
          caption
          url
        }
        title
        caption
      }
      subnavLinks {
        url
        caption
      }
    }
  }
`;
